// @flow
import type { Uploader } from "uploader";
import { getLogger } from "@cld/upload-widget";
import { selectActiveQueueMeta, selectRunningQueueMeta } from "../store/selectors";

const logger = getLogger();

const isDifferentQueueAndConfigChanged = (newQueueData: Object, runningQueueData: Object, currentUploaderQueueId: string) =>
	!runningQueueData && newQueueData // no running queue and we have an active queue
    && currentUploaderQueueId !== newQueueData.id && !newQueueData.pendingPreCalls; // latest active is different from the currently loaded one

const hasPendingPreFlagChanged = (newQueueData: Object, prevQueueData: Object) => (newQueueData
        && ((prevQueueData && !newQueueData.pendingPreCalls && prevQueueData.pendingPreCalls && newQueueData.id === prevQueueData.id)
            || (!newQueueData.pendingPreCalls && (!prevQueueData || newQueueData.id !== prevQueueData.id))));

const hasRunningPendingPreFlagChanged = (prevRunningQueueData: Object, runningQueueData: Object) =>
// running queue pending changed from true to false - could happen when crop & pre/prepare enabled & crop coordinates changed because of resize
	(prevRunningQueueData && runningQueueData && runningQueueData.id === prevRunningQueueData.id
        && !runningQueueData.pendingPreCalls && prevRunningQueueData.pendingPreCalls);

/**
 * return true in case the same active queue but config changed
 * or the pendingPreCalls flag changed to false - meaning its time to start uploading
 */
const isSameQueueAndConfigChanged = (newQueueData: Object, prevQueueData: Object, runningQueueData: Object) =>
	(!runningQueueData || (newQueueData && runningQueueData.id === newQueueData.id))
    // same active queue with different config
    && prevQueueData && newQueueData && newQueueData.id === prevQueueData.id
    && (newQueueData.config !== prevQueueData.config
        || hasPendingPreFlagChanged(newQueueData, prevQueueData));

export default (newState: Object, prevState: Object, uploader: Uploader) => {
	if (uploader.getHasRenderedUploadField()) {
		const newQueueData = selectActiveQueueMeta(newState);
		const runningQueueData = selectRunningQueueMeta(newState);
		const prevRunningQueueData = selectRunningQueueMeta(prevState);
		const prevQueueData = selectActiveQueueMeta(prevState);
		const currentUploaderQueueId = uploader.getLoadedQueueId();

		/**
         * Re-render and Reload the jquery file upload instance when queue configuration changed
         * or when a pending queue becomes non-pending
         * Then reload the pending files from that queue so they will be processed and uploaded
         */
		if (isDifferentQueueAndConfigChanged(newQueueData, runningQueueData, currentUploaderQueueId)
            || isSameQueueAndConfigChanged(newQueueData, prevQueueData, runningQueueData)
            || hasRunningPendingPreFlagChanged(prevRunningQueueData, runningQueueData)) {
			if (newQueueData) {
				logger.log(`[uploader.stateProcessors]: new queue (${newQueueData.id}) or new queue-config is active,
				 re-rending upload field and reloading pending files`);

				uploader.reRenderUploadField(newQueueData.id);
				uploader.loadFilesFromActiveQueue();
			} else if (runningQueueData) {
				logger.log(`[uploader.stateProcessors]: reloading files from pending-running queue: ${runningQueueData.id}`);

				uploader.loadFilesFromRunningQueue();
			}
		}
	}
};
